import type {
	FilterGroupKey,
	PropertyFilterGroup
} from '~/components/Filters/FiltersSidebar';
import type { PropertyTypeHighlightClickPayload } from '~/components/Filters/PropertyTypeSelect';
import type { Ref } from 'vue';

import { markRaw } from 'vue';

import PropertyArea from '~/components/Filters/PropertyArea/Area.vue';
import PropertyPrice from '~/components/Filters/PropertyPrice/Price.vue';
import PropertyTypeSelect from '~/components/Filters/PropertyTypeSelect/TypeSelect.vue';

/** Estado local do sidebar (draft) exposto pelo store. */
export interface ShortsSidebarLocalState {
	minAskingPrice: number | null;
	maxAskingPrice: number | null;
	minArea: number | null;
	maxArea: number | null;
	propertyTypes: unknown[];
}

/**
 * Retorna lista estável de grupos do sidebar de filtros Shorts.
 * extraProps usa getters/handlers para evitar recriação a cada mudança reativa (ex.: slider).
 * SSR-safe: só monta estrutura de dados, sem acessar document/window.
 */
export function useShortsSidebarFilterGroups(
	local: ShortsSidebarLocalState,
	highlightedPropertyTypes: Ref<unknown[]>,
	onClickHighlightItem?: (payload: PropertyTypeHighlightClickPayload) => void
): PropertyFilterGroup[] {
	function setMinAskingPrice(v: number | null) {
		local.minAskingPrice = v;
	}
	function setMaxAskingPrice(v: number | null) {
		local.maxAskingPrice = v;
	}
	function setMinArea(v: number | null) {
		local.minArea = v;
	}
	function setMaxArea(v: number | null) {
		local.maxArea = v;
	}
	function setPropertyTypes(v: unknown[]) {
		local.propertyTypes = v;
	}

	return [
		{
			id: 'pricing' as FilterGroupKey,
			label: 'Valor',
			keys: ['minAskingPrice', 'maxAskingPrice'],
			component: markRaw(PropertyPrice),
			extraProps: {
				get min() {
					return local.minAskingPrice;
				},
				get max() {
					return local.maxAskingPrice;
				},
				'onUpdate:min': setMinAskingPrice,
				'onUpdate:max': setMaxAskingPrice
			},
			isOpen: true,
			onUpdate: () => {}
		},
		{
			id: 'area' as FilterGroupKey,
			label: 'Área',
			keys: ['minArea', 'maxArea'],
			component: markRaw(PropertyArea),
			extraProps: {
				get min() {
					return local.minArea;
				},
				get max() {
					return local.maxArea;
				},
				'onUpdate:min': setMinArea,
				'onUpdate:max': setMaxArea
			},
			isOpen: true,
			onUpdate: () => {}
		},
		{
			id: 'type' as FilterGroupKey,
			label: 'Tipo do imóvel',
			keys: ['propertyTypes'],
			component: markRaw(PropertyTypeSelect),
			extraProps: {
				get modelValue() {
					return local.propertyTypes;
				},
				'onUpdate:modelValue': setPropertyTypes,
				separatorVariant: 'dark',
				get highlightedItems() {
					return highlightedPropertyTypes.value;
				},
				onClickHighlightItem
			},
			isOpen: true,
			onUpdate: () => {}
		}
	];
}
